import { useMemo, useCallback } from 'react'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import type { components } from '../../../../../types/api/v1'
import { useTaxTransactionOrdering } from './useTaxTransactionOrdering'

type TaxDetailRow = components['schemas']['TaxDetailRow']

// Define the expected structure for match details with tax transactions
interface MatchDetailsWithTax {
  invoice_tax_transactions?: Array<{
    tax_type?: string
    tax_description?: string
    rate?: number
    amount?: number
    product?: {
      source_name?: string
      internal_name?: string
    }
    tax_authority?: string
    tax_non_deferred?: boolean
    state?: string
    total?: number
  }>
  bol_tax_transactions?: Array<{
    tax_type?: string
    tax_description?: string
    rate?: number
    amount?: number
    product?: {
      source_name?: string
      internal_name?: string
    }
    tax_authority?: string
    tax_non_deferred?: boolean
    state?: string
    total?: number
  }>
}

/**
 * Type for tax grid display data
 */
export interface TaxGridRow {
  id: string
  tax_name: string
  tax_description: string
  type: string
  tax_authority: string
  non_deferred: string
  state: string
  amount: number // Volume field (raw number)
  product_name: string
  product_volume: null
  per_unit: number // Per unit field (raw number)
  rate: number // Rate field for per unit column
  total: number // Total field (raw number)
}

/**
 * Hook for managing tax transaction data and operations
 */
export function useMatchTaxData(matchDetails: MatchDetailsWithTax | undefined, matchId: string) {
  // Initialize ordering hooks for both invoice and BOL tax transactions
  const invoiceOrdering = useTaxTransactionOrdering<TaxGridRow>({ matchId, type: 'invoice' })
  const bolOrdering = useTaxTransactionOrdering<TaxGridRow>({ matchId, type: 'bol' })
  // Transform invoice tax data for grid display
  const invoiceTaxData = useMemo<TaxGridRow[]>(() => {
    if (!matchDetails?.invoice_tax_transactions) return []

    const rawData = matchDetails.invoice_tax_transactions.map((tax, index) => ({
      // Create unique ID by concatenating tax type, per unit, and product name
      id: `${tax.tax_type || 'unknown'}_${tax.rate || 0}_${tax.product?.internal_name || tax.product?.source_name || 'unknown'}_${index}`,
      tax_name: tax.tax_type || 'Unknown Tax',
      tax_description: tax.tax_description || 'N/A',
      type: tax.tax_type || 'Unknown',
      tax_authority: tax.tax_authority || 'Unknown',
      non_deferred: tax.tax_non_deferred === true ? 'Yes' : 'No',
      state: tax.state || 'N/A',
      // Map API fields correctly for grid columns
      amount: tax.amount || 0, // Volume: amount field as number
      product_name: tax.product?.internal_name || tax.product?.source_name || 'N/A',
      product_volume: null,
      per_unit: tax.rate || 0, // Per unit: rate field as number
      rate: tax.rate || 0, // Rate field for per unit column
      total: tax.total || 0, // Total field as number
    }))

    // Apply saved ordering from localStorage
    return invoiceOrdering.applyOrdering(rawData)
  }, [matchDetails, invoiceOrdering])

  // Transform BOL tax data for grid display
  const bolTaxData = useMemo<TaxGridRow[]>(() => {
    if (!matchDetails?.bol_tax_transactions) return []

    const rawData = matchDetails.bol_tax_transactions.map((tax, index) => ({
      // Create unique ID by concatenating tax type, per unit, and product name
      id: `${tax.tax_type || 'unknown'}_${tax.rate || 0}_${tax.product?.internal_name || tax.product?.source_name || 'unknown'}_${index}`,
      tax_name: tax.tax_type || 'Unknown Tax',
      tax_description: tax.tax_description || 'N/A',
      type: tax.tax_type || 'Unknown',
      tax_authority: tax.tax_authority || 'Unknown',
      non_deferred: tax.tax_non_deferred === true ? 'Yes' : 'No',
      state: tax.state || 'N/A',
      // Map API fields correctly for grid columns
      amount: tax.amount || 0, // Volume: amount field as number
      product_name: tax.product?.internal_name || tax.product?.source_name || 'N/A',
      product_volume: null,
      per_unit: tax.rate || 0, // Per unit: rate field as number
      rate: tax.rate || 0, // Rate field for per unit column
      total: tax.total || 0, // Total field as number
    }))

    // Apply saved ordering from localStorage
    return bolOrdering.applyOrdering(rawData)
  }, [matchDetails, bolOrdering])

  // Helper function to sort tax data by field
  const sortTaxData = useCallback((data: TaxGridRow[], field: string): TaxGridRow[] => {
    return [...data].sort((a, b) => {
      const aVal = a[field as keyof TaxGridRow]
      const bVal = b[field as keyof TaxGridRow]

      // Handle numeric sorting for amount, per_unit, rate, and total
      if (field === 'amount' || field === 'per_unit' || field === 'rate' || field === 'total') {
        const aNum = typeof aVal === 'number' ? aVal : 0
        const bNum = typeof bVal === 'number' ? bVal : 0
        return aNum - bNum
      }

      // Handle string sorting
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal)
      }

      return 0
    })
  }, [])

  return {
    invoiceTaxData,
    bolTaxData,
    sortTaxData,
    // Ordering handlers
    invoiceOrdering,
    bolOrdering,
  }
}
